import React, { memo, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { makeStyles } from '@material-ui/core/styles';
import TreeItem from '@material-ui/lab/TreeItem';
import Switch from '@material-ui/core/Switch';

import { makeHeadData, conversionCamel } from 'lib/commonLib';
import { showSuccessSnackbar } from 'modules/snackbar';
import {
  allMenuList,
  authMenuAdd,
  authMenuModify,
  authMenuDelete,
  userMapping,
  userMappingDelete,
  authUserMapping,
  groupMenuMappingDelete,
  authMenuUserGroupList,
  authGroupWrite,
  authUserWrite,
} from 'lib/api/auth';

import CurrentStatus from 'components/atoms/CurrentStatus';
import NewButton from 'components/molecules/NewButton';
import FunctionWrap from 'components/molecules/FunctionWrap';
import NewTableContainer from 'components/molecules/NewTable';
import SwitchingArea from 'components/organisms/SwitchingArea';

import MenuModal from './MenuModal.js';
import MenuTree from './MenuTree.js';

const useStyles = makeStyles((theme) => ({
  itemRoot: { padding: 5, marginTop: 10 },
  selected: { backgroundColor: 'rgb(133 183 78 / 20%)' },
  paper: { width: 200, minHeight: 230 },
}));

const Menu = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { t } = useTranslation();

  const initModal = {
    open: false,
    type: '',
    params: {},
    data: {},
  };
  const [modal, setModal] = useState(initModal);

  const initMenuList = {
    id: 'root',
    name: t('메뉴 리스트'),
    children: [],
  };
  const [mainMenuList, setMainMenuList] = useState(initMenuList);
  const [loadingMenu, setLoadingMenu] = useState(true);

  const [menuArray, setMenuArray] = useState([]);
  const [menuSeq, setMenuSeq] = useState(['0']);
  const [loading, setLoading] = useState(false);
  const [reFetch, setReFetch] = useState(false);
  const [treeReFetch, setTreeReFetch] = useState(true);

  const [selectedMenu, setSelectedMenu] = useState({});
  const [parentMenu, setParentMenu] = useState({});
  const [list, setList] = useState([{ groupList: [], userList: [] }]);

  const [groupParams, setGroupParams] = useState({});
  const [userParams, setUserParams] = useState({});

  useEffect(() => {
    if (treeReFetch) {
      setLoadingMenu(true);
      allMenuList()
        .then((response) => {
          const { data } = response.data;
          let newList = [];
          let menuArray = [];
          // eslint-disable-next-line
          data.map((menu) => {
            let newSubMenu = [];
            let menuItem = {};
            if (!!menu['SUB_MENU']) {
              // eslint-disable-next-line
              menu['SUB_MENU'].map((subMenu) => {
                let newSubMenu2 = [];
                let menuSubItem = {};
                if (!!subMenu['SUB_MENU']) {
                  // eslint-disable-next-line
                  subMenu['SUB_MENU'].map((subMenu2) => {
                    let newSubMenu3 = [];
                    let menuSubItem2 = {};
                    if (!!subMenu2['SUB_MENU']) {
                      // eslint-disable-next-line
                      subMenu2['SUB_MENU'].map((subMenu3) => {
                        newSubMenu3.push({
                          id: String(subMenu3['MENU_SEQ']),
                          name: subMenu3['MENU_NM'],
                        });
                        menuArray.push(subMenu3);
                      });
                    }
                    newSubMenu2.push({
                      id: String(subMenu2['MENU_SEQ']),
                      name: subMenu2['MENU_NM'],
                      children: newSubMenu3,
                    });
                    menuSubItem2 = subMenu2;
                    delete menuSubItem2['SUB_MENU'];
                    menuArray.push(menuSubItem2);
                  });
                }
                newSubMenu.push({
                  id: String(subMenu['MENU_SEQ']),
                  name: subMenu['MENU_NM'],
                  children: newSubMenu2,
                });
                menuSubItem = subMenu;
                delete menuSubItem['SUB_MENU'];
                menuArray.push(menuSubItem);
              });
            }
            newList.push({
              id: String(menu['MENU_SEQ']),
              name: menu['MENU_NM'],
              children: newSubMenu,
            });
            menuItem = menu;
            delete menuItem['SUB_MENU'];
            menuArray.push(menuItem);
          });
          setMainMenuList({ ...initMenuList, children: newList });
          setMenuArray(menuArray);
        })
        .catch((error) => {
          handleClickSnackbar({
            message: error.message,
            option: { type: 'error' },
          });
        })
        .finally(() => {
          setTreeReFetch(false);
          setMenuSeq(['0']);
          setLoadingMenu(false);
        });
    }
    // eslint-disable-next-line
  }, [treeReFetch]);

  useEffect(() => {
    if (reFetch) setReFetch(false);
  }, [reFetch, treeReFetch]);

  useEffect(() => {
    converParent(menuSeq);
    if (String(menuSeq) !== String(0)) handleAuthList(menuSeq);
    // eslint-disable-next-line
  }, [menuSeq]);

  const tableInfoJson = {
    group: {
      headList: [
        {
          id: 'GRP_NO',
          label: t('그룹번호'),
          orderFlag: false,
          type: 'number',
          format: '0',
          displayFlag: true,
        },
        {
          id: 'AUTH_FLAG',
          label: t('권한 여부'),
          orderFlag: null,
          type: null,
          format: null,
          displayFlag: true,
        },
        {
          id: 'GRP_NAME',
          label: t('그룹 이름'),
          orderFlag: false,
          type: 'string',
          format: '@',
          displayFlag: true,
        },
        {
          id: 'AUTH_MENU',
          label: 'AUTH_MENU',
          orderFlag: null,
          type: null,
          format: null,
          displayFlag: false,
        },
        {
          id: 'AUTH_MENU_NM',
          label: t('추가된 메뉴'),
          displayFlag: true,
          type: null,
          format: null,
          orderFlag: null,
        },
        {
          id: 'USER_AUTH_TP_CODE',
          label: t('쓰기권한'),
          displayFlag: true,
          type: null,
          format: null,
          orderFlag: null,
        },
      ],
      sort: 'desc',
      order: 'GRP_NAME',
    },
    individual: {
      headList: [
        {
          id: 'USER_NAME',
          label: t('유저명'),
          orderFlag: null,
          type: null,
          format: null,
          displayFlag: true,
        },
        {
          id: 'USER_ID',
          label: t('아이디'),
          orderFlag: false,
          type: 'string',
          format: '@',
          displayFlag: true,
        },
        {
          id: 'MENU_NO',
          label: 'MENU_NO',
          orderFlag: null,
          type: null,
          format: null,
          displayFlag: false,
        },
        {
          id: 'MENU_NAME',
          label: t('사용중인 메뉴'),
          orderFlag: null,
          type: null,
          format: null,
          displayFlag: true,
        },
        {
          id: 'USER_AUTH_TP_CODE',
          label: t('쓰기권한'),
          displayFlag: true,
          type: null,
          format: null,
          orderFlag: null,
        },
      ],
      sort: 'desc',
      order: 'USER_NAME',
    },
  };

  const customized = {
    USER_AUTH_TP_CODE: {
      makeBody: (rowObj, headsMap) => {
        return (
          <Switch
            checked={rowObj.USER_AUTH_TP_CODE !== '01'}
            onChange={() => handleWrite('group', rowObj)}
            color="primary"
          />
        );
      },
    },
  };

  const userCustomized = {
    USER_AUTH_TP_CODE: {
      makeBody: (rowObj, headsMap) => {
        return (
          <Switch
            checked={rowObj.USER_AUTH_TP_CODE !== '01'}
            onChange={() => handleWrite('user', rowObj)}
            color="primary"
          />
        );
      },
    },
  };

  const handleWrite = (type, rowObj) => {
    const isGroup = type === 'group';
    const dataReqPromise = isGroup ? authGroupWrite : authUserWrite;
    let newParams = {
      menuNo: String(menuSeq),
      writeFlag: rowObj['USER_AUTH_TP_CODE'] === '01' ? '02' : '01',
    };
    if (isGroup) newParams['grpNo'] = rowObj['GRP_NO'];
    else newParams['userId'] = rowObj['USER_ID'];

    dataReqPromise(newParams)
      .then((response) =>
        handleClickSnackbar({
          message: response.data.message,
          option: { type: 'success' },
        })
      )
      .catch((error) =>
        handleClickSnackbar({ message: error, option: { type: 'error' } })
      )
      .finally(() => {
        handleAuthList(menuSeq);
      });
  };

  const getGroupParams = (newParams) => {
    if (JSON.stringify(groupParams) !== JSON.stringify(newParams)) {
      setGroupParams(newParams);
    }
  };

  const getUserParams = (newParams) => {
    if (JSON.stringify(userParams) !== JSON.stringify(newParams)) {
      setUserParams(newParams);
    }
  };

  const handleAuthList = (propsSeq) => {
    setLoading(true);
    authMenuUserGroupList(propsSeq)
      .then((response) => {
        setList(response.data.data);
      })
      .catch((error) => {
        handleClickSnackbar({
          message: error.message,
          option: { type: 'error' },
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const renderTree = (nodes) => (
    <TreeItem
      key={nodes.id}
      nodeId={nodes.id}
      label={nodes.name}
      classes={{ root: classes.itemRoot, selected: classes.selected }}
    >
      {Array.isArray(nodes.children)
        ? nodes.children.map((node) => renderTree(node))
        : null}
    </TreeItem>
  );

  const handleModalOpen = (e) => {
    const name = e.currentTarget.name;
    let newModal = { ...initModal, open: true, type: name };
    if (name === 'add') {
      newModal = {
        ...newModal,
        modalTitle: t('메뉴 추가'),
        params: { useYn: 'Y', hirnkNo: 0, hirnkSeq: String(menuSeq) },
      };
    } else if (name === 'modify') {
      newModal = {
        ...newModal,
        modalTitle: t('메뉴 수정'),
        params: { menuNo: String(menuSeq) },
      };
    } else if (name === 'delete') {
      newModal = {
        ...newModal,
        modalTitle: t('메뉴 삭제'),
        params: { menuNo: String(menuSeq) },
      };
    } else if (name === 'userAdd') {
      newModal = {
        ...newModal,
        modalTitle: t('유저 추가'),
        params: {
          menuNos: String(menuSeq),
          userIds: modal['params']['userIds'],
        },
      };
    }
    setModal(newModal);
  };

  const handleModalClose = () => {
    setModal({ ...modal, open: false, params: {} });
  };

  const handleModalData = (newModal) => {
    setModal(newModal);
  };

  const handleSubmit = () => {
    if (modal['type'] === 'add') {
      handleReqPromise(authMenuAdd, modal.params, true, false);
    } else if (modal['type'] === 'modify') {
      handleReqPromise(
        authMenuModify,
        {
          menuSeq: modal.params['menuSeq'],
          hirnkSeq: modal.params['hirnkSeq'],
          menuNm: modal.params['menuNm'],
          menuUrl: modal.params['menuUrl'],
          useYn: modal.params['useYn'],
          menuSortNo: modal.params['menuSortNo'],
        },
        true,
        false
      );
    } else if (modal['type'] === 'delete') {
      handleReqPromise(authMenuDelete, menuSeq, true, false);
    } else if (modal['type'] === 'userAdd') {
      handleReqPromise(userMapping, modal['params'], false, true);
    } else if (modal['type'] === 'userDelete') {
      handleReqPromise(userMappingDelete, modal['params'], false, true);
    } else if (modal['type'] === 'edit') {
      handleReqPromise(authUserMapping, modal['params'], false, true);
    } else if (modal['type'] === 'mappingDelete') {
      let newParams = { ...modal['params'] };
      delete newParams['mappingFlag'];
      handleReqPromise(groupMenuMappingDelete, newParams, false, true);
    }
  };

  const handleGroupHeadCallBack = (type, data) => {
    let tmpExlNo = [];
    data.forEach(function (rowData) {
      tmpExlNo.push(rowData['GRP_NO']);
    });
    setModal({
      ...initModal,
      open: true,
      type: type,
      modalTitle: t('권한 수정'),
      params: { menuNos: String(menuSeq), grpNos: String(tmpExlNo) },
    });
  };

  const handleUserHeadCallBack = (type, data) => {
    let tmpExlNo = [];
    data.forEach(function (rowData) {
      tmpExlNo.push(rowData['USER_ID']);
    });
    setModal({
      ...initModal,
      open: true,
      type: 'userDelete',
      modalTitle: t('유저 권한 삭제'),
      params: { menuNos: String(menuSeq), userIds: String(tmpExlNo) },
    });
  };

  const handleNodeSelect = (event, nodeIds) => {
    if (String(nodeIds) !== String(menuSeq)) {
      setMenuSeq(nodeIds);
      setReFetch(true);
    }
  };

  const converParent = (seq) => {
    let tempMenu = menuArray.filter((menu) => findMenu(menu, 'MENU_SEQ', seq));
    tempMenu = tempMenu[0];
    let newMenu = {};
    let newParentMenu = {};
    for (let menuKey in tempMenu) {
      newMenu[conversionCamel(menuKey)] = tempMenu[menuKey];
    }
    newMenu['hirnkSeq'] = newMenu['menuHirnkSeq'];
    delete newMenu['menuHirnkSeq'];
    if (!!newMenu['hirnkSeq']) {
      let tempParentMenu = menuArray.filter((menu) =>
        findMenu(menu, 'MENU_SEQ', newMenu['hirnkSeq'])
      );
      tempParentMenu = tempParentMenu[0];
      for (let menuKey in tempParentMenu) {
        newParentMenu[conversionCamel(menuKey)] = tempParentMenu[menuKey];
      }
      newParentMenu['hirnkSeq'] = newParentMenu['menuHirnkSeq'];
      delete newParentMenu['menuHirnkSeq'];
    }
    setSelectedMenu(newMenu);
    setParentMenu(newParentMenu);
  };

  const findMenu = (menu, key, value) => {
    if (Number(value) === menu[key]) {
      return true;
    }
  };

  const handleReqPromise = (
    request,
    parameter,
    treeFetchFlag = false,
    reFetchFlag = false
  ) => {
    request(parameter)
      .then((response) => {
        handleClickSnackbar({
          message: response.data.message,
          option: { type: 'success' },
        });
      })
      .catch((error) => {
        handleClickSnackbar({ message: error, option: { type: 'error' } });
      })
      .finally(() => {
        handleModalClose();
        if (treeFetchFlag) setTreeReFetch(true);
        if (reFetchFlag) {
          handleAuthList(menuSeq);
          setReFetch(true);
        }
      });
  };

  const handleClickSnackbar = useCallback((snackbarOptions) => {
    dispatch(showSuccessSnackbar(snackbarOptions));
    //eslint-disable-next-line
  }, []);

  return (
    <>
      <FunctionWrap loading={false}>
        <NewButton
          type="filterButton"
          name="add"
          onClick={(e) => handleModalOpen(e)}
          disableRipple
          disabled={!menuSeq || menuSeq.length !== 1}
        >
          {t('메뉴 추가')}
        </NewButton>
        <NewButton
          type="filterButton"
          name="modify"
          onClick={(e) => handleModalOpen(e)}
          disableRipple
          disabled={
            !menuSeq ||
            menuSeq.length !== 1 ||
            String(menuSeq) === '0' ||
            String(menuSeq) === 'root'
          }
        >
          {t('메뉴 수정')}
        </NewButton>
        <NewButton
          type="filterButton"
          name="delete"
          onClick={(e) => handleModalOpen(e)}
          disableRipple
          disabled={
            !menuSeq ||
            menuSeq.length !== 1 ||
            String(menuSeq) === '0' ||
            String(menuSeq) === 'root'
          }
        >
          {t('메뉴 삭제')}
        </NewButton>
        <NewButton
          type="filterButton"
          name="userAdd"
          onClick={(e) => handleModalOpen(e)}
          disableRipple
          disabled={
            !menuSeq || String(menuSeq) === '0' || String(menuSeq) === 'root'
          }
        >
          {t('유저 추가')}
        </NewButton>
      </FunctionWrap>
      <div style={{ display: 'flex', minHeight: 500 }}>
        <div
          style={{
            position: 'relative',
            width: '30%',
            marginRight: '1%',
            paddingLeft: 10,
          }}
        >
          {loadingMenu ? (
            <div style={{ height: 200 }}>
              <CurrentStatus />
            </div>
          ) : (
            <MenuTree
              handleNodeSelect={handleNodeSelect}
              renderTree={renderTree}
              mainMenuList={mainMenuList}
              loading={loading}
            />
          )}
        </div>
        <div style={{ width: '69%' }}>
          <SwitchingArea title={t('그룹 권한 리스트')}>
            {!!menuSeq && String(menuSeq) !== '0' ? (
              <NewTableContainer
                getParam={getGroupParams}
                params={groupParams}
                useCustomTable
                propsList={list.groupList}
                propsHeadCells={makeHeadData(tableInfoJson.group.headList)}
                useTotal={false}
                usePagenation={false}
                rowsPerPage={-1}
                useExcel={false}
                useFullScreen={false}
                useCheckBox
                customized={customized}
                hasActController={{ edit: true }}
                headCallBack={handleGroupHeadCallBack}
                loading={loading}
                reFetch={reFetch}
                useStoreLoading={false}
              />
            ) : (
              <CurrentStatus type="noData" />
            )}
          </SwitchingArea>
          <SwitchingArea title={t('유저 권한 리스트')}>
            {!!menuSeq && String(menuSeq) !== '0' ? (
              <NewTableContainer
                params={userParams}
                getParam={getUserParams}
                useCustomTable
                propsList={list.userList}
                propsHeadCells={makeHeadData(tableInfoJson.individual.headList)}
                useTotal={false}
                usePagenation={false}
                rowsPerPage={-1}
                useExcel={false}
                useFullScreen={false}
                useCheckBox
                customized={userCustomized}
                hasActController={{ delete: true }}
                headCallBack={handleUserHeadCallBack}
                loading={loading}
                reFetch={reFetch}
                useStoreLoading={false}
              />
            ) : (
              <CurrentStatus type="noData" />
            )}
          </SwitchingArea>
        </div>
      </div>
      {modal.open && (
        <MenuModal
          modal={modal}
          handleModalClose={handleModalClose}
          handleModalData={handleModalData}
          handleSubmit={handleSubmit}
          mainMenuList={mainMenuList}
          handleClickSnackbar={handleClickSnackbar}
          selectedMenu={selectedMenu}
          parentMenu={parentMenu}
          menuArray={menuArray}
        />
      )}
    </>
  );
};

export default memo(Menu);
